import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import classNames from "classnames";
import { useAuth } from "../lib/auth/useAuth";
import { useUiStore } from "../state/uiStore";
import { useLists, useMyFeed, useCreateList } from "../hooks/useTasks";
import { isOpenStatus } from "../lib/taskVM";
import { isoToDayKey } from "../lib/date";
import { MiniCalendar } from "./MiniCalendar";
import { Avatar } from "./Avatar";

const NAV = [
  { to: "/", label: "Mes tâches", icon: "◔" },
  { to: "/assignees", label: "Assignées à moi", icon: "◎" },
  { to: "/notifications", label: "Notifications", icon: "◷" },
  { to: "/calendrier", label: "Calendrier", icon: "▦" },
  { to: "/board", label: "Board", icon: "▤" },
  { to: "/equipe", label: "Équipe", icon: "◫" },
  { to: "/contacts", label: "Contacts", icon: "◉" },
] as const;

export function Sidebar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    dayFilter,
    setDayFilter,
    listFilter,
    setListFilter,
    toggleSidebar,
    prefs,
  } = useUiStore();
  const lists = useLists();
  const feed = useMyFeed();
  const createList = useCreateList();
  const [addingList, setAddingList] = useState(false);
  const [listName, setListName] = useState("");

  const openItems = (feed.data ?? []).filter((f) => isOpenStatus(f.status));
  const openCount = openItems.length;
  const dueCount: Record<string, number> = {};
  for (const f of openItems) {
    if (!f.due_date) continue;
    const k = isoToDayKey(f.due_date);
    dueCount[k] = (dueCount[k] ?? 0) + 1;
  }

  const submitList = () => {
    const name = listName.trim();
    if (!name) return;
    createList.mutate(
      { name, visibility: prefs.defaultVisibility },
      { onSuccess: () => setListName("") },
    );
  };

  const toggleListFilter = (id: string) => {
    setListFilter(
      listFilter.includes(id)
        ? listFilter.filter((x) => x !== id)
        : [...listFilter, id],
    );
    navigate("/");
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-logo">T</div>
        <div className="sidebar-title">Todo</div>
        <div className="sidebar-subtitle">Senacle</div>
        <button
          className="sidebar-collapse"
          onClick={toggleSidebar}
          title="Replier"
        >
          ‹‹
        </button>
      </div>
      <div className="sidebar-scroll">
        <nav className="sidebar-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                classNames("nav-item", { active: isActive })
              }
            >
              <span className="nav-icon">{n.icon}</span>
              {n.label}
              {n.to === "/" && openCount > 0 && (
                <span className="nav-count">{openCount}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-section">
          Listes
          <button
            onClick={() => setAddingList((v) => !v)}
            style={{
              marginLeft: "auto",
              color: "var(--ink-ghost)",
              cursor: "pointer",
              fontSize: 13,
            }}
            aria-label="Ajouter une liste"
          >
            ＋
          </button>
        </div>
        <nav className="sidebar-nav">
          {(lists.data ?? []).map((l) => (
            <button
              key={l.id}
              className={classNames("nav-item", {
                active: listFilter.includes(l.id),
              })}
              onClick={() => toggleListFilter(l.id)}
            >
              <span className="nav-icon">{l.icon || "≡"}</span>
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {l.name}
              </span>
            </button>
          ))}
          {addingList && (
            <div className="quick-add" style={{ margin: "4px 8px" }}>
              <input
                autoFocus
                value={listName}
                placeholder="Nom de la liste"
                onChange={(e) => setListName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitList();
                  if (e.key === "Escape") setAddingList(false);
                }}
                aria-label="Nom de la nouvelle liste"
              />
            </div>
          )}
          <NavLink
            to="/listes"
            className={({ isActive }) =>
              classNames("nav-item", { active: isActive })
            }
          >
            <span className="nav-icon">⚙</span>
            Gérer les listes
          </NavLink>
        </nav>

        <div className="sidebar-section">Agenda</div>
        <MiniCalendar
          dueCount={dueCount}
          selected={dayFilter}
          onSelect={(days) => {
            setDayFilter(days);
            if (days.length) navigate("/");
          }}
          weekStart={prefs.weekStart}
        />
      </div>
      <NavLink
        to="/profil"
        className="sidebar-footer"
        style={{ textDecoration: "none", color: "inherit" }}
      >
        <Avatar
          name={user?.profile?.name ?? user?.profile?.preferred_username ?? "?"}
          size={24}
        />
        <span
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {user?.profile?.name ?? user?.profile?.preferred_username ?? "Profil"}
        </span>
      </NavLink>
    </aside>
  );
}
